import { Input, ListGroup } from '@readcloud/component-library';
import { Switch } from '@readcloud/ui';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ListHeader } from './Annotations/EditComponents/ListHeader';
import { Label } from '@readcloud/ui';
export interface SelectableObjectItem {
  avatarName?: string;
  primaryText: React.ReactNode;
  secondaryText: string;
  id: string;
}
interface Props {
  selectAllObjectName?: string;
  items: SelectableObjectItem[];
  selectedItems: string[];
  setSelectedItems: (selectedItems: string[]) => void;
}

/**
 * A selectable material-ui list (checkbox on the right of each item) that contains an optional avatar (a full name like Jane Doe will give JD), a primary text and secondary text
 *
 * - Will also present a header, containing your "Title" and a button to control select/deselect all
 *  - "Select/Deselect All" behavior works like:
 *    - If NONE are selected, select all
 *    - If SOME are selected, select all
 *    - If ALL are selected, deselect all
 */
export const SelectableObjectsList: React.FC<Props> = (props) => {
  const key = uuidv4();
  const [allChecked, setAllChecked] = React.useState<boolean>();

  const onToggleAll = React.useCallback(() => {
    //This feels excessive.
    if (props.selectedItems.length === props.items.length) {
      props.setSelectedItems([]);
    } else {
      props.setSelectedItems(props.items.map((item) => item.id));
    }
  }, [props.selectedItems.length, props.setSelectedItems, props.items.length]);

  const onToggle = React.useCallback(
    (itemId: string) => {
      const tempSelectedItems = [...props.selectedItems];
      if (tempSelectedItems.includes(itemId)) {
        tempSelectedItems.splice(tempSelectedItems.indexOf(itemId), 1);
      } else {
        tempSelectedItems.push(itemId);
      }
      props.setSelectedItems(tempSelectedItems);
    },
    [props.selectedItems.length, props.setSelectedItems]
  );

  React.useEffect(() => {
    if (props.selectedItems.length === props.items.length) {
      setAllChecked(true);
    } else {
      setAllChecked(false);
    }
  }, [props.selectedItems.length, props.items.length]);

  const selectAll = React.useMemo(
    () => (
      <div className="rc-flex rc-justify-between rc-items-center">
        <Label className="rc-font-bold">{`Select All ${props.selectAllObjectName}`}</Label>
        <Switch checked={allChecked} onCheckedChange={onToggleAll} />
      </div>
    ),
    [props.selectAllObjectName, allChecked, onToggleAll]
  );

  const items = React.useMemo(
    () =>
      props.items.map((item) => {
        return (
          <ListGroup.Item
            key={'listitem-' + item.id}
            action
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggle(item.id);
            }}
          >
            <div className="rc-flex rc-items-center rc-justify-between">
              <div className="rc-flex rc-flex-col rc-pr-2">
                {item.primaryText}
                {item.secondaryText ? ' (' + item.secondaryText + ')' : ''}
              </div>
              <Switch
                checked={props.selectedItems?.includes(item.id) || false}
                onCheckedChange={() => onToggle(item.id)}
              />
            </div>
          </ListGroup.Item>
        );
      }),
    [props.selectedItems, props.items.length]
  );

  return (
    <div className="rc-flex rc-flex-col first-letter rc-w-full">
      <ListGroup variant="flush" className="rc-w-full rc-flex">
        {selectAll}
        {items}
      </ListGroup>
    </div>
  );
};
